/**
 * Role Permission Matrix — admin UI for configuring which permissions
 * each role has. Rows = roles, columns = permissions grouped by scope
 * (Company / Project / Personal).
 *
 * Read/write via the backend engine in utils/permissions.py.
 * Saving a row PUTs to /api/admin/roles/{role}/permissions which upserts
 * a row in `role_permissions` (company-scoped).
 *
 * Studio Manager comes seeded with project_*=ON + company_settings + team
 * + company_*=OFF + Corner-of-Desk=OFF + Super Admin=OFF. The principal
 * designer can flip any checkbox here.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield, Lock, FolderOpen, User as UserIcon, RotateCcw, Save, Home, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SCOPE_META = {
  company: { label: 'Company / Back of the House', icon: Lock, accent: 'amber' },
  project: { label: 'Project Level', icon: FolderOpen, accent: 'blue' },
  personal: { label: 'Personal', icon: UserIcon, accent: 'purple' },
};

const SCOPE_ORDER = ['company', 'project', 'personal'];

export default function RolePermissionsMatrix() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]); // [{ key, scope, label, description }]
  const [roles, setRoles] = useState([]);     // [{ role, permissions: [], customized, updated_at }]
  const [draft, setDraft] = useState({});     // { role: Set<perm> }
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/api/admin/permissions/catalog`),
        axios.get(`${API}/api/admin/roles`),
      ]);
      setCatalog(c.data.permissions || []);
      setRoles(r.data.roles || []);
      const next = {};
      (r.data.roles || []).forEach((row) => {
        next[row.role] = new Set(row.permissions || []);
      });
      setDraft(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load role permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Group catalog by scope for the column headers.
  const byScope = useMemo(() => {
    const out = { company: [], project: [], personal: [] };
    catalog.forEach((p) => {
      if (out[p.scope]) out[p.scope].push(p);
    });
    return out;
  }, [catalog]);

  const isDirty = (role) => {
    const saved = roles.find((r) => r.role === role);
    if (!saved) return false;
    const cur = draft[role] || new Set();
    const savedSet = new Set(saved.permissions || []);
    if (cur.size !== savedSet.size) return true;
    for (const k of cur) if (!savedSet.has(k)) return true;
    return false;
  };

  const toggle = (role, permKey) => {
    if (role === 'principal_designer') return;
    setDraft((prev) => {
      const next = { ...prev };
      const cur = new Set(next[role] || []);
      if (cur.has(permKey)) cur.delete(permKey);
      else cur.add(permKey);
      next[role] = cur;
      return next;
    });
  };

  const save = async (role) => {
    setSaving((p) => ({ ...p, [role]: true }));
    try {
      const perms = Array.from(draft[role] || []);
      await axios.put(`${API}/api/admin/roles/${role}/permissions`, { permissions: perms });
      toast.success(`Saved ${role.replace('_', ' ')}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving((p) => ({ ...p, [role]: false }));
    }
  };

  const resetToDefault = async (role) => {
    if (!window.confirm(`Reset ${role.replace('_', ' ')} permissions to defaults?`)) return;
    setSaving((p) => ({ ...p, [role]: true }));
    try {
      await axios.post(`${API}/api/admin/roles/${role}/reset-to-default`);
      toast.success(`Reset ${role.replace('_', ' ')}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset');
    } finally {
      setSaving((p) => ({ ...p, [role]: false }));
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-muted-foreground">Loading role permissions…</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-2 text-sm mb-4" data-testid="breadcrumb-nav">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1 text-muted-foreground hover:text-[#BC5D41] transition-colors"
          data-testid="breadcrumb-my-desk"
        >
          <Home className="w-4 h-4" />
          My Desk
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <button
          onClick={() => navigate('/admin')}
          className="text-muted-foreground hover:text-[#BC5D41] transition-colors"
          data-testid="breadcrumb-admin"
        >
          Admin
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="font-medium text-[#1A1918]">Roles &amp; Permissions</span>
      </nav>

      <Card data-testid="role-permissions-matrix">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-accent" />
              Roles & Permissions
            </CardTitle>
            <CardDescription>
              Configure which permissions each role has. Permissions are scoped:
              <span className="font-medium"> Project-level</span> (e.g. budget for a specific project)
              and <span className="font-medium">Company-level</span> (e.g. company-wide P&amp;L) are
              independent — a role can have one without the other.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {roles.map((row) => {
            const role = row.role;
            const cur = draft[role] || new Set();
            const dirty = isDirty(role);
            const isPrincipal = role === 'principal_designer';
            return (
              <div
                key={role}
                className="border rounded-lg p-4"
                data-testid={`role-row-${role}`}
              >
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold capitalize">{role.replace(/_/g, ' ')}</h3>
                      {row.customized && (
                        <Badge variant="outline" className="text-[10px]">Customized</Badge>
                      )}
                      {isPrincipal && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
                          Locked (Admin)
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {cur.size} of {catalog.length} permissions
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {row.customized && !isPrincipal && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetToDefault(role)}
                        disabled={saving[role]}
                        data-testid={`role-reset-${role}`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Reset
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={dirty ? 'default' : 'outline'}
                      onClick={() => save(role)}
                      disabled={!dirty || saving[role] || isPrincipal}
                      data-testid={`role-save-${role}`}
                    >
                      <Save className="w-3 h-3 mr-1" />
                      {saving[role] ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {SCOPE_ORDER.map((scope) => {
                    const meta = SCOPE_META[scope];
                    const perms = byScope[scope] || [];
                    const Icon = meta.icon;
                    if (perms.length === 0) return null;
                    return (
                      <div key={scope} className="space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          <Icon className="w-3.5 h-3.5" />
                          {meta.label}
                        </div>
                        <div className="space-y-1.5">
                          {perms.map((p) => {
                            const checked = cur.has(p.key);
                            return (
                              <label
                                key={p.key}
                                className={`flex items-start gap-2 text-sm cursor-pointer hover:bg-accent/5 px-2 py-1.5 rounded ${
                                  isPrincipal ? 'opacity-70 cursor-not-allowed' : ''
                                }`}
                                title={p.description}
                              >
                                <Checkbox
                                  checked={checked}
                                  disabled={isPrincipal}
                                  onCheckedChange={() => toggle(role, p.key)}
                                  data-testid={`role-perm-${role}-${p.key}`}
                                />
                                <span className="flex-1">
                                  <div className="leading-tight">{p.label}</div>
                                  <div className="text-[11px] text-muted-foreground leading-snug">
                                    {p.description}
                                  </div>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
      </Card>
    </div>
  );
}
